import {NgModule} from '@angular/core';

import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {NgxSpinnerModule} from "ngx-spinner";

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {AccordionModule} from 'ngx-bootstrap/accordion';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {CollapseModule} from 'ngx-bootstrap/collapse';

import {FormsModule, ReactiveFormsModule} from '@angular/forms';

import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from '@azure/msal-browser';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService
} from '@azure/msal-angular';

import {environment} from 'src/environments/environment';
import {AuthCallbackComponent} from './auth-callback/auth-callback.component';
import {HomeComponent} from './home/home.component';
import {LoginComponent} from './login/login.component';
import {AuthLayoutComponent} from "./layouts/auth-layout/auth-layout.component";
import {PortalLayoutComponent} from './layouts/portal-layout/portal-layout.component';

import {ComponentsModule} from "./components/components.module";
import {RegistrationLandingComponent} from './pages/registration/registration-landing/registration-landing.component';
import {RegistrationFormComponent} from './pages/registration/registration-form/registration-form.component';
import {OrganizationComponent} from './pages/organization/organization.component';
import {ContactComponent} from './pages/contact/contact.component';
import {AuthredirectComponent} from './pages/authredirect/authredirect.component';
import {PaginationModule} from "ngx-bootstrap/pagination";
import {ApplicationDetailComponent} from './pages/applications/application-detail/application-detail.component';
import {ApplicationComponent} from "./pages/applications/application/application.component";
import {BsDatepickerModule} from "ngx-bootstrap/datepicker";
import {TasksComponent} from './pages/applications/application-detail/tasks/tasks.component';
import {ReportDueComponent} from './pages/applications/application-detail/report-due/report-due.component';
import {SubmittedReport} from './pages/applications/application-detail/submitted-report/submitted-report';
import {ModalModule} from "ngx-bootstrap/modal";
import {PdfViewerModule} from "ng2-pdf-viewer";
import {ApplicationReportComponent} from "./pages/applications/application-report/application-report.component";
import {CommonModule} from '@angular/common';
import {QuestionAndAnswerComponent} from './pages/question-and-answer/question-and-answer.component';
import {RegistrationResultComponent} from './pages/registration/registration-result/registration-result.component';
import {PaymentInfoComponent} from './pages/organization/payment-info/payment-info.component';
import {HttpErrorInterceptor} from "./helper/http-error.interceptor";
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import {RoleBasedGuard} from "./role-based.guard";
import { ExternalContactComponent } from './pages/external-contact/external-contact.component';
import { ExternalPaymentinfoComponent } from './pages/external-contact/external-paymentinfo/external-paymentinfo.component';
import { ExternalActiveAssignmentComponent } from './pages/external-active-assignment/external-active-assignment.component';
import { ExternalActiveAssitnmentDetailComponent } from './pages/external-active-assitnment-detail/external-active-assitnment-detail.component';
import {ProjectsModules} from "./pages/projects/projects.module";
import { EmergencyFormDetailComponent } from './pages/applications/application-detail/emergency-form-detail/emergency-form-detail.component';
import { AssesementUploadComponent } from './pages/registration/registration-form/assesement-upload/assesement-upload.component';
import { VerifyPassportComponent } from './verify-passport/verify-passport.component';
import { AccountLockRedirectComponent } from './pages/account-lock-redirect/account-lock-redirect.component';
import { LoginFailedComponent } from './pages/login-failed/login-failed.component';
import { AccountDeactivatedComponent } from './pages/account-deactivated/account-deactivated.component';
import { AnonymousVerifyPassportComponent } from './pages/anonymous-verify-passport/anonymous-verify-passport.component';



export function loggerCallback(_: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  console.log('config', environment)
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
      redirectUri: '/authredirect',
      postLogoutRedirectUri: '/',
      knownAuthorities: [environment.b2cPolicies.authorityDomain],
      navigateToLoginRequestUrl: false
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string> | null>();
  protectedResourceMap.set(`${environment.apiConfig.uri}/api/common/currencies`, null);
  protectedResourceMap.set(`${environment.apiConfig.uri}/api/common/countries`, null);
  protectedResourceMap.set(`${environment.apiConfig.uri}/api/common/countriesentity`, null);
  protectedResourceMap.set(`${environment.apiConfig.uri}/api/common/contactmethod`, null);
  protectedResourceMap.set(`${environment.apiConfig.uri}/api/organization/register`, null);
  protectedResourceMap.set(`${environment.apiConfig.uri}/api/common/industries`, null);
  protectedResourceMap.set(`${environment.apiConfig.uri}/api/user/contactVerify`, null);
  protectedResourceMap.set(`${environment.apiConfig.uri}/api/document/registerForm`, null);
  protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/login-failed'
  };
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    LoginComponent,
    AuthCallbackComponent,
    AuthLayoutComponent,
    PortalLayoutComponent,
    RegistrationLandingComponent,
    RegistrationFormComponent,
    OrganizationComponent,
    ContactComponent,
    AuthredirectComponent,
    ApplicationComponent,
    ApplicationDetailComponent,
    ApplicationReportComponent,
    TasksComponent,
    ReportDueComponent,
    SubmittedReport,
    QuestionAndAnswerComponent,
    RegistrationResultComponent,
    PaymentInfoComponent,
    UnauthorizedComponent,
    ExternalContactComponent,
    ExternalPaymentinfoComponent,
    ExternalActiveAssignmentComponent,
    ExternalActiveAssitnmentDetailComponent,
    EmergencyFormDetailComponent,
    AssesementUploadComponent,
    VerifyPassportComponent,
    AccountLockRedirectComponent,
    LoginFailedComponent,
    AccountDeactivatedComponent,
    AnonymousVerifyPassportComponent,
  ],
  imports: [
    CommonModule,
    BrowserAnimationsModule,
    AccordionModule.forRoot(),
    BsDropdownModule.forRoot(),
    CollapseModule.forRoot(),
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    MsalModule,
    ReactiveFormsModule,
    ComponentsModule,
    NgxSpinnerModule,
    PaginationModule,
    FormsModule,
    BsDatepickerModule,
    ModalModule.forRoot(),
    PdfViewerModule,
    ProjectsModules
  ],
  providers: [{
    provide: HTTP_INTERCEPTORS,
    useClass: MsalInterceptor,
    multi: true,
  },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    RoleBasedGuard],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule {
}
